"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ShoppingBag, X } from "lucide-react";
import CartSummary from "./CartSummary";
import { BookingStep } from "@/types/booking";

interface CartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CartDialog({ open, onOpenChange }: CartDialogProps) {
  const {
    selectedServices,
    selectedPackages,
    giftCardCart,
    businessInfo,
    handleNextStep,
  } = useBooking();

  const cartCount =
    selectedServices.length + selectedPackages.length + (giftCardCart ? 1 : 0);
  const isGiftCardPurchase = !!giftCardCart;
  const isVoucherPurchase = selectedPackages.length > 0 && !giftCardCart;

  const handleCheckout = () => {
    onOpenChange(false);
    if (isVoucherPurchase) {
      handleNextStep(BookingStep.VOUCHER_CHECKOUT);
      return;
    }
    if (isGiftCardPurchase) {
      handleNextStep(BookingStep.GIFT_CARD_CHECKOUT);
      return;
    }
    const nextStep = businessInfo?.allowServiceStaffSelection
      ? BookingStep.STAFF_SELECTION
      : BookingStep.TIME_AND_DATE;
    handleNextStep(nextStep);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-hidden" showCloseButton={false}>
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold text-grey-900">
              My Cart ({cartCount})
            </DialogTitle>
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 hover:bg-grey-50 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-grey-500" />
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {cartCount === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="h-16 w-16 text-grey-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-grey-900 mb-2">
                Your cart is empty
              </h3>
              <p className="text-grey-600 mb-6">
                Add services or packages to get started with your booking
              </p>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-green-300 hover:bg-green-400 text-white"
              >
                Browse Services
              </Button>
            </div>
          ) : (
            <CartSummary
              showButton={true}
              customButton={{
                text: isVoucherPurchase || isGiftCardPurchase ? "Checkout" : "Continue to booking",
                onClick: handleCheckout,
                className: "w-full bg-green-300 hover:bg-green-600 text-white",
              }}
              showDateTime={true}
              showMembershipBanner={true}
              showLocation={true}
              showPromoCode={false}
              showFullBreakdown={true}
              title=""
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
